/**
 * Короткий деликатный «дзинь» для всплывашки обновлений «Что нового».
 *
 * Синтезируем звук через Web Audio API прямо в браузере — без внешнего
 * аудио-файла. Это надёжно проходит CSP nginx-монолита (никаких запросов к
 * сторонним хостам и data:-ассетов), работает офлайн и не тянет лишний бинарь
 * в бандл. Звук намеренно тихий и короткий (~0.3с, две ноты вверх) — это
 * уведомление, а не сигнал тревоги.
 *
 * Autoplay-политика браузеров запрещает звук до первого жеста пользователя.
 * Поэтому один раз ловим самый первый pointer/key/touch и «разблокируем»
 * AudioContext — дальше все `playAnnouncementChime()` звучат сразу. Любые
 * ошибки глушим: звук — необязательное украшение показа, попап и без него
 * остаётся гарантией.
 */

type AudioContextCtor = typeof AudioContext;

// Один AudioContext на вкладку — создаём лениво (создавать до жеста незачем,
// часть браузеров стартует его в состоянии 'suspended').
let ctx: AudioContext | null = null;
let unlockBound = false;

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor: AudioContextCtor | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

/**
 * Разблокировать аудио на первом же взаимодействии пользователя со страницей.
 * Вызывается один раз при загрузке модуля; слушатели одноразовые и снимают
 * себя сами. До этого момента `playAnnouncementChime()` просто молчит.
 */
function bindUnlockOnce(): void {
  if (unlockBound || typeof window === 'undefined') return;
  unlockBound = true;
  const unlock = () => {
    const ac = audioContext();
    if (ac && ac.state === 'suspended') void ac.resume().catch(() => {});
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    window.removeEventListener('touchstart', unlock);
  };
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
  window.addEventListener('touchstart', unlock, { once: true });
}

bindUnlockOnce();

/** Проиграть одну синус-ноту с мягкой атакой/затуханием (без щелчков). */
function tone(
  ac: AudioContext,
  freq: number,
  startAt: number,
  durationSec: number,
  peakGain: number,
): void {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  // Экспоненциальная огибающая: быстрая атака + плавный спад. Мгновенный
  // старт/стоп синуса дал бы слышимый «клик» — поэтому рампы, а не setValue.
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peakGain, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSec);
  osc.connect(gain).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + durationSec + 0.02);
}

/**
 * Мягкий двухнотный сигнал появления всплывашки обновления (E5 → A5).
 * Идемпотентность на стороне вызывающего (один звук на новый анонс). Ничего
 * не возвращает и не бросает: если Web Audio недоступен или жеста ещё не было
 * — просто молчит.
 */
export function playAnnouncementChime(): void {
  const ac = audioContext();
  if (!ac) return;
  const play = () => {
    const now = ac.currentTime;
    tone(ac, 659.25, now, 0.18, 0.14); // E5
    tone(ac, 880.0, now + 0.1, 0.22, 0.12); // A5
  };
  if (ac.state === 'suspended') {
    // Жеста ещё не было — попробуем разбудить; если политика не даёт, молчим.
    ac.resume().then(play).catch(() => {});
  } else {
    play();
  }
}
